// ============================================================================
// 📁 FILE: frontend/src/modules/ticket/components/detail/TicketHeader.js
// 🎨 Premium Gradient Hero Header (Purple → Pink)
// ============================================================================

import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const fmt = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const statusBadgeClass = (status) => {
  switch ((status || "").toLowerCase()) {
    case "new": return "tf-badge tf-badge-new";
    case "assigned": return "tf-badge tf-badge-assigned";
    case "in_progress": return "tf-badge tf-badge-inprogress";
    case "on_hold": return "tf-badge tf-badge-onhold";
    case "waiting_for_customer": return "tf-badge tf-badge-waiting";
    case "resolved": return "tf-badge tf-badge-resolved";
    case "closed": return "tf-badge tf-badge-closed";
    case "reopened": return "tf-badge tf-badge-reopened";
    default: return "tf-badge tf-badge-default";
  }
};

const TicketHeader = ({
  ticket = {},
  onOpenAssign = () => {},
  onEditClick = () => {},
  className = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const ticketNumber =
    ticket.ticket_number || ticket.ticketNumber || `#${ticket.id}` || "-";

  const title = ticket.title || "-";
  const status = ticket.status || "new";
  const priority = ticket.priority || "";
  const severity = ticket.severity || "";
  const createdAt = ticket.created_at || ticket.createdAt;
  const updatedAt = ticket.updated_at || ticket.updatedAt;

  // ============================
  // 🔥 FIXED: extract .name safely
  // ============================
  const reporter =
    ticket?.reported_by?.name ||
    ticket?.reported_by_name ||
    ticket?.reported_by_id ||
    "-";

  const assignee =
    ticket?.assigned_to?.name ||
    ticket?.assigned_to_name ||
    ticket?.assigned_to_id ||
    t("ticket.detail.unassigned");

  // ===========================================================
  // RENDER
  // ===========================================================
  return (
    <div className={`tf-ticket-hero ${className}`}>

      {/* BACK BUTTON */}
      <button className="tf-back-btn" onClick={() => navigate(-1)}>
        <ArrowLeftOutlined /> {t("ticket.detail.back")}
      </button>

      {/* HERO MAIN CONTENT */}
      <div className="tf-hero-main">

        {/* LEFT CONTENT */}
        <div className="tf-hero-left">
          <div className="tf-hero-number">{ticketNumber}</div>
          <h1 className="tf-hero-title">{title}</h1>

          <div className="tf-hero-badges">
            <span className={statusBadgeClass(status)}>
              {t(`ticket.status.${status}`) || status}
            </span>

            {priority && (
              <span className="tf-pill tf-pill-priority">
                {t(`ticket.priority.${priority}`) || priority}
              </span>
            )}

            {severity && (
              <span className="tf-pill tf-pill-severity">
                {t(`ticket.severity.${severity}`) || severity}
              </span>
            )}
          </div>

          <div className="tf-hero-meta">
            <span><strong>{t("ticket.detail.reported_by")}:</strong> {reporter}</span>
            <span><strong>{t("ticket.detail.created_at")}:</strong> {fmt(createdAt)}</span>
            <span><strong>{t("ticket.detail.updated_at")}:</strong> {fmt(updatedAt)}</span>
            <span><strong>{t("ticket.detail.assigned_to")}:</strong> {assignee}</span>
          </div>
        </div>

        {/* RIGHT — ACTION BUTTONS */}
        <div className="tf-hero-actions">

          {/* EDIT BUTTON */}
          <button className="tf-hero-action-btn" onClick={onEditClick}>
            {t("ticket.actions.edit")}
          </button>

          {/* ASSIGN USER BUTTON */}
          <button className="tf-hero-action-btn" onClick={onOpenAssign}>
            {t("ticket.detail.assign_user")}
          </button>

        </div>

      </div>
    </div>
  );
};

TicketHeader.propTypes = {
  ticket: PropTypes.object,
  onOpenAssign: PropTypes.func,
  onEditClick: PropTypes.func,
  className: PropTypes.string,
};

export default TicketHeader;
